import { TrendingUp, TrendingDown, Wallet, Coins, Scale, Percent } from "lucide-react";
import { nok } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";

function Trend({ value }) {
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
        up ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-destructive/10 text-destructive"
      }`}
    >
      <Icon className="h-3 w-3" strokeWidth={2} /> {up ? "+" : ""}{value}%
    </span>
  );
}

const FIG_ICON = { bank: Wallet, omsetning: Coins, driftsresultat: Scale, margin: Percent };

export function OverviewTab({ data, live }) {
  if (!data) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {(data.key_figures || []).map((f) => {
        const Icon = FIG_ICON[f.key] || Coins;
        return (
          <div key={f.key} data-testid={`figure-${f.key}`} className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={1.7} />
              </div>
              <span className="rounded-md bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {live ? "Live" : "Demo"}
              </span>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{f.label} · {f.period}</p>
            <p className="mt-1 font-headings text-3xl font-extrabold tracking-tight">
              {f.unit === "%" ? `${f.value} %` : (f.value == null ? "—" : nok(f.value))}
            </p>
            {f.key === "bank" && f.value == null ? (
              <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="bank-unavailable-note">
                {data.source?.bank_note || "Banksaldo ikke tilgjengelig fra regnskapssystemet"}
              </p>
            ) : (
              <div className="mt-3"><Trend value={f.trend} /> <span className="text-xs text-muted-foreground">mot i fjor</span></div>
            )}
          </div>
        );
      })}

      {/* Balanse mini */}
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:col-span-2 lg:col-span-3">
        <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Balanse (sammendrag)</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {[
            ["Eiendeler", data.balance?.eiendeler],
            ["Egenkapital", data.balance?.egenkapital],
            ["Gjeld", data.balance?.gjeld],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-muted/50 p-4">
              <p className="text-xs text-muted-foreground">{k}</p>
              <p className="mt-1 font-headings text-xl font-extrabold tabular-nums">{nok(v)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
